/**
 * Qualification Matcher - Client-side qualification matching logic
 * Matches user qualifications to assignment requirements using dual-key strategy
 */

/**
 * Build dual-key lookup maps for fast qualification matching
 * Primary: Direct requirement_id match
 * Secondary: Cross-space course_link match
 *
 * @param {Array} qualifications - Array of user qualification objects
 * @returns {Object} Maps object with byRequirementId and byCourseLink
 */
export function buildQualificationMaps(qualifications) {
  const byRequirementId = new Map();
  const byCourseLink = new Map();

  qualifications.forEach(q => {
    // Primary map: Direct requirement match
    const reqKey = `${q.user_id}-${q.requirement_id}`;
    byRequirementId.set(reqKey, q);

    // Secondary map: Course link match (for cross-space qualifications)
    if (q.course_link_id && q.course_link_type) {
      const courseKey = `${q.user_id}-${q.course_link_id}-${q.course_link_type}`;

      const existing = byCourseLink.get(courseKey);
      if (!existing || shouldReplaceQualification(existing, q)) {
        byCourseLink.set(courseKey, q);
      }
    }
  });

  return { byRequirementId, byCourseLink };
}

/**
 * Determine if new qualification should replace existing one
 * Priority: valid > expired > pending
 *
 * @param {Object} existing - Existing qualification
 * @param {Object} newQual - New qualification to compare
 * @returns {boolean} True if newQual should replace existing
 */
function shouldReplaceQualification(existing, newQual) {
  const getPriority = (q) => {
    if (q.status === 'completed' && (!q.expiry_date || new Date(q.expiry_date) > new Date())) {
      return 3; // Valid
    }
    if (q.status === 'completed' && q.expiry_date && new Date(q.expiry_date) <= new Date()) {
      return 2; // Expired
    }
    return 1; // Pending/other
  };

  return getPriority(newQual) > getPriority(existing);
}

/**
 * Find qualification for an assignment
 * Tries direct match first, then course_link match
 *
 * @param {Object} assignment - Assignment object
 * @param {Object} requirement - Requirement object
 * @param {Object} qualificationMaps - Maps from buildQualificationMaps
 * @returns {Object|null} Matching qualification or null
 */
export function findQualification(assignment, requirement, qualificationMaps) {
  // Try primary match (same requirement_id)
  let qualification = qualificationMaps.byRequirementId.get(
    `${assignment.team_member_id}-${assignment.requirement_id}`
  );

  // Try secondary match (course_link for cross-space)
  if (!qualification && requirement?.course_link_id) {
    const courseKey = `${assignment.team_member_id}-${requirement.course_link_id}-${requirement.course_link_type}`;
    qualification = qualificationMaps.byCourseLink.get(courseKey);
  }

  return qualification || null;
}

/**
 * Calculate effective status for an assignment based on qualification
 *
 * @param {Object|null} qualification - Qualification object or null
 * @returns {string} Status: 'valid', 'expired', 'pending', or qualification.status
 */
export function calculateEffectiveStatus(qualification) {
  if (!qualification) return 'pending';

  if (qualification.status === 'completed') {
    if (!qualification.expiry_date || new Date(qualification.expiry_date) > new Date()) {
      return 'valid';
    }
    if (new Date(qualification.expiry_date) <= new Date()) {
      return 'expired';
    }
  }

  return qualification.status;
}

/**
 * Enrich assignments with qualification data and computed fields
 * This is the main function that combines all the matching logic
 *
 * @param {Array} assignments - Raw assignment objects
 * @param {Array} requirements - Requirement objects
 * @param {Array} qualifications - User qualification objects
 * @returns {Array} Enriched assignment objects with qualification data
 */
export function enrichAssignments(assignments, requirements, qualifications) {
  const qualificationMaps = buildQualificationMaps(qualifications);

  const requirementsMap = new Map();
  requirements.forEach(r => requirementsMap.set(r.id, r));

  return assignments.map(assignment => {
    const requirement = requirementsMap.get(assignment.requirement_id);
    const qualification = findQualification(assignment, requirement, qualificationMaps);
    const effectiveStatus = calculateEffectiveStatus(qualification);

    return {
      ...assignment,
      requirement,
      qualification,
      effectiveStatus,
      hasQualification: !!qualification,
      qualifiedFromOtherSpace: qualification && qualification.requirement_id !== assignment.requirement_id,
      isAssigned: assignment.assigned === true
    };
  });
}
